/**
 * Notification Hook - Toast notifications
 * შეტყობინებების ჰუქი
 */

import { create } from 'zustand';
import type { NotificationType } from '../types';

interface NotificationState {
  notifications: NotificationType[];
  add: (notification: Omit<NotificationType, 'id'>) => string;
  remove: (id: string) => void;
  clear: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],

  add: (notification) => {
    const id = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const newNotification: NotificationType = {
      ...notification,
      id,
      duration: notification.duration ?? 5000,
    };

    set((state) => ({
      notifications: [...state.notifications, newNotification],
    }));

    // Auto-remove after duration
    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        get().remove(id);
      }, newNotification.duration);
    }

    return id;
  },

  remove: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },

  clear: () => {
    set({ notifications: [] });
  },
}));

/**
 * useNotification hook
 */
export function useNotification() {
  const { notifications, add, remove, clear } = useNotificationStore();

  const success = (title: string, message?: string) => {
    return add({ type: 'success', title, message });
  };

  const error = (title: string, message?: string) => {
    return add({ type: 'error', title, message, duration: 8000 });
  };

  const warning = (title: string, message?: string) => {
    return add({ type: 'warning', title, message });
  };

  const info = (title: string, message?: string) => {
    return add({ type: 'info', title, message });
  };

  return {
    notifications,
    add,
    remove,
    clear,
    success,
    error,
    warning,
    info,
  };
}
